import React, { useCallback, useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import {
  Scale, Plus, Trash2, Save, Loader2, Rocket, FileText, History, Lock, ShieldCheck,
  ArrowUp, ArrowDown, Copy, Pencil, Check, X,
} from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { BlockSection } from "./LegalTemplateNodes";

// V1.23/V1.24 · Editor de cláusulas legales con árbol jerárquico ilimitado.
// Regla inviolable: LO QUE EL USUARIO ESCRIBE = LO QUE SE GUARDA = LO QUE SE
// IMPRIME. La numeración es puramente estructural (auto-calculada).

const STATUS_LABEL = { active: "ACTIVA", draft: "BORRADOR", archived: "ARCHIVADA" };
const STATUS_STYLE = {
  active:   "bg-emerald-100 text-emerald-800 border-emerald-300",
  draft:    "bg-amber-100 text-amber-800 border-amber-300",
  archived: "bg-neutral-100 text-neutral-600 border-neutral-300",
};
const FALLBACK_STYLE = "bg-neutral-100 text-neutral-600 border-neutral-300";

// V1.37.4 · Capítulos dinámicos. Cada capítulo tiene un `id`, `title`, `slug`,
// `order` y su propio árbol `clauses`. Los 5 capítulos legacy se conservan
// con IDs estables (`legacy_clauses_*`) y sus cláusulas se ESPEJAN a los
// campos legacy en el guardado para no romper la generación de PDFs
// (snapshot V1.22) ni la lógica de la versión activa.
const LEGACY_CHAPTER_MAP = {
  legacy_clauses_main:          "clauses_main",
  legacy_clauses_particulares:  "clauses_particulares",
  legacy_clauses_economic:      "clauses_economic",
  legacy_clauses_desistimiento: "clauses_desistimiento",
  legacy_clauses_lopd:          "clauses_lopd",
};
const DEFAULT_LEGACY_TITLES = {
  legacy_clauses_main:          "Condiciones generales",
  legacy_clauses_particulares:  "Condiciones particulares",
  legacy_clauses_economic:      "Condiciones económicas",
  legacy_clauses_desistimiento: "Derecho de desistimiento",
  legacy_clauses_lopd:          "Protección de datos (LOPD)",
};
const LEGACY_ORDER = [
  "legacy_clauses_main",
  "legacy_clauses_particulares",
  "legacy_clauses_economic",
  "legacy_clauses_desistimiento",
  "legacy_clauses_lopd",
];

function ActivateDialog({ open, onClose, versionLabel, onConfirm, submitting, ackText }) {
  const [accepted, setAccepted] = useState(false);
  const [typed, setTyped] = useState("");
  useEffect(() => {
    if (!open) { setAccepted(false); setTyped(""); }
  }, [open]);
  const canProceed = accepted && typed === ackText;
  return (
    <Dialog open={open} onOpenChange={(o) => !o && !submitting && onClose && onClose()}>
      <DialogContent className="sm:max-w-lg" data-testid="activate-legal-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Rocket className="h-5 w-5 text-orange-500" />
            Publicar versión {versionLabel}
          </DialogTitle>
          <DialogDescription>
            Al activar esta versión, los futuros contratos usarán este texto. Los contratos históricos NO se ven afectados (snapshot V1.22).
          </DialogDescription>
        </DialogHeader>
        <div className="rounded-lg border-2 border-amber-300 bg-amber-50/70 p-3 text-sm text-amber-900 space-y-2">
          <p className="font-semibold">Confirmación de responsabilidad</p>
          <p className="text-xs leading-relaxed">
            La aplicación NO revisa, corrige ni interpreta el contenido legal. El texto guardado es exactamente el que has introducido y se imprimirá tal cual en los contratos.
          </p>
        </div>
        <label className="flex items-start gap-2 cursor-pointer text-sm">
          <input
            type="checkbox"
            checked={accepted}
            onChange={(e) => setAccepted(e.target.checked)}
            className="mt-1"
            data-testid="ack-checkbox"
          />
          <span className="text-neutral-800">{ackText}</span>
        </label>
        <div>
          <label className="text-[11px] font-semibold text-neutral-600 uppercase tracking-wide block mb-1">
            Escribe la frase anterior para confirmar
          </label>
          <textarea
            value={typed}
            onChange={(e) => setTyped(e.target.value)}
            rows={3}
            className="w-full rounded-md border border-neutral-300 px-2 py-1.5 text-xs font-mono"
            placeholder={ackText}
            data-testid="ack-typed"
          />
          {typed && typed !== ackText ? (
            <p className="mt-1 text-[10px] text-red-600">
              La frase no coincide exactamente. Debe copiarse verbatim.
            </p>
          ) : null}
        </div>
        <DialogFooter>
          <button
            type="button"
            disabled={submitting}
            onClick={onClose}
            className="rounded-md border border-neutral-300 px-3 py-1.5 text-sm hover:bg-neutral-50 disabled:opacity-50"
          >Cancelar</button>
          <button
            type="button"
            disabled={!canProceed || submitting}
            onClick={onConfirm}
            className="inline-flex items-center gap-1.5 rounded-md bg-orange-600 hover:bg-orange-700 text-white px-4 py-1.5 text-sm font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="confirm-activate-btn"
          >
            {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Rocket className="h-4 w-4" />}
            Publicar
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function AuditDialog({ open, onClose, templateId, versionLabel }) {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    if (!open || !templateId) return;
    setLoading(true);
    api.get("/legal-templates/audit", { params: { template_id: templateId } })
      .then(({ data }) => setLogs(data || []))
      .catch((e) => toast.error(formatApiError(e && e.response && e.response.data && e.response.data.detail)))
      .finally(() => setLoading(false));
  }, [open, templateId]);
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose && onClose()}>
      <DialogContent className="sm:max-w-xl" data-testid="legal-audit-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <History className="h-5 w-5 text-orange-500" />
            Auditoría — {versionLabel}
          </DialogTitle>
          <DialogDescription>Registro inmutable de acciones sobre esta versión.</DialogDescription>
        </DialogHeader>
        {loading ? (
          <div className="flex justify-center py-6"><Loader2 className="h-5 w-5 animate-spin text-orange-500" /></div>
        ) : logs.length === 0 ? (
          <p className="text-sm text-neutral-500 italic">Sin eventos registrados todavía.</p>
        ) : (
          <ul className="space-y-1.5 max-h-[50vh] overflow-y-auto pr-1">
            {logs.map((l) => (
              <li key={l.id} className="rounded-md border border-neutral-200 bg-neutral-50 px-3 py-2 text-xs">
                <div className="flex items-center justify-between gap-2 font-mono">
                  <span className="font-semibold text-orange-700">{l.action.toUpperCase()}</span>
                  <span className="text-neutral-500">{new Date(l.at).toLocaleString("es-ES")}</span>
                </div>
                <div className="text-neutral-700 mt-0.5">
                  <span className="font-semibold">{l.actor}</span>
                  {l.meta && Object.keys(l.meta).length > 0 ? (
                    <span className="ml-2 text-[10px] text-neutral-500 font-mono">
                      {JSON.stringify(l.meta)}
                    </span>
                  ) : null}
                </div>
              </li>
            ))}
          </ul>
        )}
      </DialogContent>
    </Dialog>
  );
}

function normalizeTree(arr) {
  return (arr || []).map((c) => ({
    title: c.title || "",
    body:  c.body  || "",
    children: normalizeTree(c.children || []),
  }));
}

// V1.37.4 · Construye el array de capítulos dinámicos a partir de una versión.
// - Si `selected.chapters` viene del backend, se toma como fuente autoritativa.
// - Si el capítulo tiene id legacy (`legacy_clauses_*`) pero sus `clauses`
//   están vacíos, se rehidrata desde el campo legacy correspondiente
//   (fallback defensivo para versiones aún no migradas por completo).
// - Si `selected.chapters` no existe (versión legacy pura), se materializa
//   in-memory desde los 5 campos legacy manteniendo el orden clásico.
function buildChapters(selected) {
  const raw = Array.isArray(selected.chapters) && selected.chapters.length > 0
    ? selected.chapters
    : LEGACY_ORDER.map((id, order) => ({
        id,
        title: DEFAULT_LEGACY_TITLES[id],
        slug:  LEGACY_CHAPTER_MAP[id],
        order,
        clauses: selected[LEGACY_CHAPTER_MAP[id]] || [],
      }));
  const list = raw.map((c, i) => {
    const legacyKey = LEGACY_CHAPTER_MAP[c.id];
    const rawClauses = (c.clauses && c.clauses.length)
      ? c.clauses
      : (legacyKey ? (selected[legacyKey] || []) : []);
    return {
      id: c.id || ("ch_" + i),
      title: c.title || DEFAULT_LEGACY_TITLES[c.id] || "Capítulo",
      slug: c.slug || "",
      order: typeof c.order === "number" ? c.order : i,
      clauses: normalizeTree(rawClauses),
      isLegacy: !!legacyKey,
    };
  });
  list.sort((a, b) => (a.order || 0) - (b.order || 0));
  return list.map((c, i) => ({ ...c, order: i }));
}

function countAllClauses(arr) {
  return (arr || []).reduce((acc, n) => acc + 1 + countAllClauses(n.children || []), 0);
}

// V1.37.4 · Fila de capítulo dinámico. Encapsula:
//   - Cabecera con título editable inline (modo "rename").
//   - Botones de: subir, bajar, duplicar, eliminar (solo en borradores).
//   - Cuerpo con el editor jerárquico `BlockSection` (sin modificar).
// Nota: se mantiene el mismo contador de cláusulas ("countAll" en BlockSection)
// y la misma UX visual — solo se añaden controles a nivel capítulo.
function ChapterRow({
  chapter, index, total, disabled, defaultOpen,
  onRename, onMove, onDuplicate, onDelete, onSetClauses,
}) {
  const [renaming, setRenaming] = useState(false);
  const [tempTitle, setTempTitle] = useState(chapter.title || "");
  useEffect(() => { if (!renaming) setTempTitle(chapter.title || ""); }, [chapter.title, renaming]);
  const commitRename = () => {
    const t = (tempTitle || "").trim();
    if (!t) { toast.error("El nombre no puede estar vacío."); return; }
    if (t !== chapter.title) onRename(chapter.id, t);
    setRenaming(false);
  };
  const cancelRename = () => { setTempTitle(chapter.title || ""); setRenaming(false); };
  const block = { key: chapter.id, label: "Cláusulas" };
  const isLegacy = !!chapter.isLegacy;

  return (
    <div className="rounded-xl border border-neutral-200 bg-white overflow-hidden" data-testid={"chapter-row-" + chapter.id}>
      <div className="flex items-center justify-between gap-2 px-3 py-2 border-b border-neutral-200 bg-neutral-50/60">
        <div className="flex items-center gap-2 flex-1 min-w-0">
          <span className="inline-flex items-center rounded-md bg-orange-100 text-orange-800 font-mono text-[10px] font-bold px-1.5 py-0.5 shrink-0" data-testid={"chapter-order-" + chapter.id}>
            {index + 1}
          </span>
          {renaming ? (
            <div className="flex items-center gap-1 flex-1 min-w-0">
              <input
                type="text"
                value={tempTitle}
                onChange={(e) => setTempTitle(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") { e.preventDefault(); commitRename(); }
                  if (e.key === "Escape") { e.preventDefault(); cancelRename(); }
                }}
                autoFocus
                className="flex-1 min-w-0 rounded-md border border-orange-400 bg-white px-2 py-1 text-sm font-semibold"
                data-testid={"chapter-title-input-" + chapter.id}
              />
              <button
                type="button"
                onClick={commitRename}
                className="p-1 rounded hover:bg-emerald-100 text-emerald-700"
                title="Guardar nombre"
                data-testid={"chapter-rename-save-" + chapter.id}
              ><Check className="h-4 w-4" /></button>
              <button
                type="button"
                onClick={cancelRename}
                className="p-1 rounded hover:bg-neutral-100 text-neutral-600"
                title="Cancelar"
                data-testid={"chapter-rename-cancel-" + chapter.id}
              ><X className="h-4 w-4" /></button>
            </div>
          ) : (
            <span className="text-sm font-semibold text-neutral-800 truncate" data-testid={"chapter-title-" + chapter.id}>
              {chapter.title}
              {isLegacy ? (
                <span className="ml-2 text-[9px] font-mono text-neutral-500 uppercase tracking-wide">original</span>
              ) : null}
            </span>
          )}
        </div>
        {!disabled && !renaming ? (
          <div className="flex items-center gap-0.5 shrink-0">
            <button
              type="button"
              onClick={() => setRenaming(true)}
              className="p-1 rounded hover:bg-neutral-100 text-neutral-700"
              title="Renombrar"
              data-testid={"chapter-rename-btn-" + chapter.id}
            ><Pencil className="h-3.5 w-3.5" /></button>
            <button
              type="button"
              onClick={() => onMove(chapter.id, -1)}
              disabled={index === 0}
              className="p-1 rounded hover:bg-neutral-100 text-neutral-700 disabled:opacity-30 disabled:cursor-not-allowed"
              title="Subir capítulo"
              data-testid={"chapter-up-" + chapter.id}
            ><ArrowUp className="h-3.5 w-3.5" /></button>
            <button
              type="button"
              onClick={() => onMove(chapter.id, 1)}
              disabled={index >= total - 1}
              className="p-1 rounded hover:bg-neutral-100 text-neutral-700 disabled:opacity-30 disabled:cursor-not-allowed"
              title="Bajar capítulo"
              data-testid={"chapter-down-" + chapter.id}
            ><ArrowDown className="h-3.5 w-3.5" /></button>
            <button
              type="button"
              onClick={() => onDuplicate(chapter.id)}
              className="p-1 rounded hover:bg-blue-50 text-blue-700"
              title="Duplicar capítulo"
              data-testid={"chapter-duplicate-" + chapter.id}
            ><Copy className="h-3.5 w-3.5" /></button>
            <button
              type="button"
              onClick={() => onDelete(chapter.id)}
              disabled={total <= 1}
              className="p-1 rounded hover:bg-red-50 text-red-600 disabled:opacity-30 disabled:cursor-not-allowed"
              title={total <= 1 ? "Debe existir al menos un capítulo" : "Eliminar capítulo"}
              data-testid={"chapter-delete-" + chapter.id}
            ><Trash2 className="h-3.5 w-3.5" /></button>
          </div>
        ) : null}
      </div>
      <BlockSection
        block={block}
        clauses={chapter.clauses || []}
        disabled={disabled}
        defaultOpen={defaultOpen}
        onSetClauses={onSetClauses}
      />
    </div>
  );
}

export default function LegalTemplatesEditor() {
  const [versions, setVersions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState(null);
  const [draft, setDraft] = useState(null);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [activating, setActivating] = useState(false);
  const [showActivate, setShowActivate] = useState(false);
  const [showAudit, setShowAudit] = useState(false);
  const [ackText, setAckText] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const listPromise = api.get("/legal-templates/versions");
      const ackPromise  = api.get("/legal-templates/acknowledgment-text");
      const [{ data: list }, { data: ack }] = await Promise.all([listPromise, ackPromise]);
      setVersions(list || []);
      setAckText(ack && ack.text ? ack.text : "");
      const active = (list || []).find((v) => v.status === "active");
      const firstDraft = (list || []).find((v) => v.status === "draft");
      setSelectedId((prev) => prev || (active && active.id) || (firstDraft && firstDraft.id) || (list && list[0] && list[0].id) || null);
    } catch (e) {
      toast.error(formatApiError(e && e.response && e.response.data && e.response.data.detail));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const selected = useMemo(
    () => versions.find((v) => v.id === selectedId) || null,
    [versions, selectedId]
  );

  useEffect(() => {
    if (!selected) { setDraft(null); setDirty(false); return; }
    setDraft({
      version: selected.version || "",
      summary: selected.summary || "",
      clauses_main:          normalizeTree(selected.clauses_main),
      clauses_particulares:  normalizeTree(selected.clauses_particulares),
      clauses_economic:      normalizeTree(selected.clauses_economic),
      clauses_desistimiento: normalizeTree(selected.clauses_desistimiento),
      clauses_lopd:          normalizeTree(selected.clauses_lopd),
      chapters:              buildChapters(selected),
    });
    setDirty(false);
  }, [selected]);

  const isEditable = selected && selected.status === "draft";

  // V1.37.4 · Setter de cláusulas por capítulo (dinámico). Mantiene el
  // espejo del campo legacy correspondiente actualizado si el capítulo es
  // uno de los 5 originales — así los PDFs siguen funcionando exactamente
  // igual y la lógica de snapshot inmutable no se ve alterada.
  const setChapterClauses = (chapterId) => (arr) => {
    setDraft((d) => {
      const chapters = (d.chapters || []).map((c) =>
        c.id === chapterId ? { ...c, clauses: arr } : c
      );
      const patch = { chapters };
      const legacyKey = LEGACY_CHAPTER_MAP[chapterId];
      if (legacyKey) patch[legacyKey] = arr;
      return { ...d, ...patch };
    });
    setDirty(true);
  };

  const renameChapter = (chapterId, nextTitle) => {
    const title = (nextTitle || "").trim();
    if (!title) { toast.error("El nombre del capítulo no puede estar vacío."); return; }
    setDraft((d) => ({
      ...d,
      chapters: (d.chapters || []).map((c) =>
        c.id === chapterId ? { ...c, title } : c
      ),
    }));
    setDirty(true);
  };

  const moveChapter = (chapterId, delta) => {
    setDraft((d) => {
      const list = [...(d.chapters || [])];
      const idx = list.findIndex((c) => c.id === chapterId);
      const j = idx + delta;
      if (idx < 0 || j < 0 || j >= list.length) return d;
      [list[idx], list[j]] = [list[j], list[idx]];
      return { ...d, chapters: list.map((c, i) => ({ ...c, order: i })) };
    });
    setDirty(true);
  };

  const duplicateChapter = (chapterId) => {
    setDraft((d) => {
      const list = [...(d.chapters || [])];
      const idx = list.findIndex((c) => c.id === chapterId);
      if (idx < 0) return d;
      const src = list[idx];
      const dup = {
        id: "ch_" + Math.random().toString(36).slice(2, 10) + Date.now().toString(36),
        title: src.title + " (copia)",
        slug: "",
        order: idx + 1,
        clauses: normalizeTree(src.clauses),
        isLegacy: false,
      };
      list.splice(idx + 1, 0, dup);
      return { ...d, chapters: list.map((c, i) => ({ ...c, order: i })) };
    });
    setDirty(true);
  };

  const deleteChapter = (chapterId) => {
    const chapters = (draft && draft.chapters) || [];
    if (chapters.length <= 1) {
      toast.error("Debe existir al menos un capítulo.");
      return;
    }
    const target = chapters.find((c) => c.id === chapterId);
    if (!target) return;
    const clauseCount = countAllClauses(target.clauses);
    const warn = clauseCount > 0
      ? "El capítulo \"" + target.title + "\" contiene " + clauseCount + " cláusula(s). Se eliminarán junto con el capítulo. ¿Continuar?"
      : "¿Eliminar el capítulo \"" + target.title + "\"?";
    if (!window.confirm(warn)) return;
    setDraft((d) => {
      const remaining = (d.chapters || []).filter((c) => c.id !== chapterId)
        .map((c, i) => ({ ...c, order: i }));
      const patch = { chapters: remaining };
      // Si es legacy, vaciar también el campo legacy espejo (mantiene coherencia).
      const legacyKey = LEGACY_CHAPTER_MAP[chapterId];
      if (legacyKey) patch[legacyKey] = [];
      return { ...d, ...patch };
    });
    setDirty(true);
  };

  const addChapter = () => {
    const title = (window.prompt("Nombre del nuevo capítulo:", "") || "").trim();
    if (!title) return;
    setDraft((d) => {
      const list = [...(d.chapters || [])];
      const newCh = {
        id: "ch_" + Math.random().toString(36).slice(2, 10) + Date.now().toString(36),
        title,
        slug: "",
        order: list.length,
        clauses: [],
        isLegacy: false,
      };
      list.push(newCh);
      return { ...d, chapters: list.map((c, i) => ({ ...c, order: i })) };
    });
    setDirty(true);
  };

  const handleSaveDraft = async () => {
    if (!selected || !draft) return;
    if (!isEditable) { toast.error("Solo se pueden editar borradores."); return; }
    setSaving(true);
    try {
      // V1.37.4 · Sincroniza campos legacy con las cláusulas de los
      // capítulos legacy actuales (si siguen existiendo). Los PDFs y la
      // versión activa continúan leyendo desde `clauses_*` — el snapshot
      // inmutable y la auditoría permanecen 100% intactos.
      const chapters = (draft.chapters || []).map((c, i) => ({
        id: c.id,
        title: c.title,
        slug: c.slug || "",
        order: i,
        clauses: c.clauses || [],
      }));
      const legacyPayload = {
        clauses_main:          draft.clauses_main || [],
        clauses_particulares:  draft.clauses_particulares || [],
        clauses_economic:      draft.clauses_economic || [],
        clauses_desistimiento: draft.clauses_desistimiento || [],
        clauses_lopd:          draft.clauses_lopd || [],
      };
      chapters.forEach((c) => {
        const legacyKey = LEGACY_CHAPTER_MAP[c.id];
        if (legacyKey) legacyPayload[legacyKey] = c.clauses || [];
      });
      const payload = {
        version: draft.version,
        summary: draft.summary,
        ...legacyPayload,
        chapters,
      };
      await api.patch("/legal-templates/versions/" + selected.id, payload);
      toast.success("Borrador guardado (texto verbatim, sin modificaciones automáticas).");
      setDirty(false);
      await load();
    } catch (e) {
      toast.error(formatApiError(e && e.response && e.response.data && e.response.data.detail));
    } finally { setSaving(false); }
  };

  const handleCreateDraftFrom = async (baseVersion) => {
    const base = baseVersion || selected;
    if (!base) return;
    setSaving(true);
    try {
      const payload = {
        version: "",
        summary: "Borrador basado en " + base.version,
        clauses_main:          base.clauses_main || [],
        clauses_particulares:  base.clauses_particulares || [],
        clauses_economic:      base.clauses_economic || [],
        clauses_desistimiento: base.clauses_desistimiento || [],
        clauses_lopd:          base.clauses_lopd || [],
      };
      const { data } = await api.post("/legal-templates/versions", payload);
      toast.success("Nuevo borrador " + data.version + " creado.");
      await load();
      setSelectedId(data.id);
    } catch (e) {
      toast.error(formatApiError(e && e.response && e.response.data && e.response.data.detail));
    } finally { setSaving(false); }
  };

  const handleDeleteDraft = async () => {
    if (!selected || selected.status !== "draft") return;
    if (!window.confirm("¿Eliminar el borrador " + selected.version + "? Esta acción no se puede deshacer.")) return;
    setSaving(true);
    try {
      await api.delete("/legal-templates/versions/" + selected.id);
      toast.success("Borrador " + selected.version + " eliminado.");
      setSelectedId(null);
      await load();
    } catch (e) {
      toast.error(formatApiError(e && e.response && e.response.data && e.response.data.detail));
    } finally { setSaving(false); }
  };

  const handleActivate = async () => {
    if (!selected || dirty) { toast.error("Guarda el borrador antes de publicarlo."); return; }
    setActivating(true);
    try {
      await api.post("/legal-templates/versions/" + selected.id + "/activate", { acknowledgment: ackText });
      toast.success("Versión " + selected.version + " publicada. Los contratos futuros la usarán.");
      setShowActivate(false);
      await load();
    } catch (e) {
      toast.error(formatApiError(e && e.response && e.response.data && e.response.data.detail));
    } finally { setActivating(false); }
  };

  if (loading) {
    return (
      <div className="rounded-2xl border border-neutral-200 bg-white p-6 flex items-center justify-center">
        <Loader2 className="h-5 w-5 animate-spin text-orange-500" />
      </div>
    );
  }

  const statusStyle = selected ? (STATUS_STYLE[selected.status] || FALLBACK_STYLE) : FALLBACK_STYLE;
  const statusLabel = selected ? (STATUS_LABEL[selected.status] || selected.status) : "";

  return (
    <div className="rounded-2xl border border-neutral-200 bg-white p-4 md:p-6 space-y-4" data-testid="legal-templates-editor">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <Scale className="h-5 w-5 text-orange-500" />
          <h2 className="font-bold text-base">Cláusulas legales</h2>
          <span className="text-[10px] font-mono rounded bg-orange-100 text-orange-800 px-1.5 py-0.5">V1.24</span>
        </div>
        <button
          type="button"
          onClick={() => handleCreateDraftFrom(selected || versions.find((v) => v.status === "active"))}
          disabled={saving || !versions.length}
          className="inline-flex items-center gap-1.5 rounded-lg bg-orange-600 hover:bg-orange-700 text-white px-3 py-1.5 text-xs font-semibold disabled:opacity-50"
          data-testid="new-draft-btn"
        >
          <Plus className="h-3.5 w-3.5" /> Nueva versión (borrador)
        </button>
      </div>

      <div className="rounded-lg border-2 border-emerald-200 bg-emerald-50/60 p-3 text-xs text-emerald-900 flex items-start gap-2">
        <ShieldCheck className="h-4 w-4 shrink-0 mt-0.5" />
        <div>
          <p className="font-semibold">Gestor documental — sin IA ni edición automática.</p>
          <p className="text-[11px] leading-relaxed mt-0.5">
            Lo que escribes se guarda tal cual y se imprime tal cual. La aplicación no revisa, corrige, reformula ni resume el contenido legal.
            La numeración (6, 6.1, 6.1.1, …) es puramente estructural y se calcula automáticamente a partir de la posición en el árbol.
            Los contratos históricos quedan protegidos por el snapshot legal (V1.22) y no cambian aunque publiques versiones nuevas.
          </p>
        </div>
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-[11px] font-semibold text-neutral-600 uppercase tracking-wide">Versión:</span>
        <select
          value={selectedId || ""}
          onChange={(e) => setSelectedId(e.target.value)}
          className="rounded-md border border-neutral-300 px-2 py-1 text-sm bg-white"
          data-testid="version-select"
        >
          {versions.map((v) => {
            const label = STATUS_LABEL[v.status] || v.status;
            return (
              <option key={v.id} value={v.id}>
                {v.version} · {label} · {new Date(v.created_at).toLocaleDateString("es-ES")}
              </option>
            );
          })}
        </select>
        {selected ? (
          <>
            <span
              data-testid={"legal-status-" + selected.status}
              className={"inline-flex items-center rounded-md border px-1.5 py-0.5 text-[10px] font-bold tracking-wide " + statusStyle}
            >
              {statusLabel}
            </span>
            <button
              type="button"
              onClick={() => setShowAudit(true)}
              className="inline-flex items-center gap-1 rounded-md border border-neutral-300 hover:bg-neutral-50 px-2 py-1 text-xs"
              data-testid="show-audit-btn"
            >
              <History className="h-3.5 w-3.5" /> Auditoría
            </button>
          </>
        ) : null}
      </div>

      {selected && draft ? (
        <>
          <div className="grid md:grid-cols-2 gap-3">
            <div>
              <label className="text-[11px] font-semibold text-neutral-600 uppercase tracking-wide block mb-1">
                Nombre / Nº de versión
              </label>
              <input
                type="text"
                value={draft.version}
                onChange={(e) => { setDraft((d) => ({ ...d, version: e.target.value })); setDirty(true); }}
                disabled={!isEditable}
                className="w-full rounded-md border border-neutral-300 px-2 py-1.5 text-sm font-mono disabled:bg-neutral-50"
                data-testid="draft-version-input"
              />
            </div>
            <div>
              <label className="text-[11px] font-semibold text-neutral-600 uppercase tracking-wide block mb-1">
                Motivo / Nota interna
              </label>
              <input
                type="text"
                value={draft.summary}
                onChange={(e) => { setDraft((d) => ({ ...d, summary: e.target.value })); setDirty(true); }}
                disabled={!isEditable}
                placeholder='ej: "Actualización artículo 102 RDL 1/2007"'
                className="w-full rounded-md border border-neutral-300 px-2 py-1.5 text-sm disabled:bg-neutral-50"
                data-testid="draft-summary-input"
              />
            </div>
          </div>

          <div className="grid md:grid-cols-4 gap-2 text-[11px]">
            <div className="rounded-md bg-neutral-50 border border-neutral-200 px-2 py-1.5">
              <div className="text-neutral-500">Creada</div>
              <div className="font-mono">{new Date(selected.created_at).toLocaleString("es-ES")}</div>
              <div className="text-neutral-500">{selected.created_by}</div>
            </div>
            {selected.updated_at ? (
              <div className="rounded-md bg-neutral-50 border border-neutral-200 px-2 py-1.5">
                <div className="text-neutral-500">Última edición</div>
                <div className="font-mono">{new Date(selected.updated_at).toLocaleString("es-ES")}</div>
                <div className="text-neutral-500">{selected.updated_by}</div>
              </div>
            ) : null}
            {selected.activated_at ? (
              <div className="rounded-md bg-emerald-50 border border-emerald-200 px-2 py-1.5">
                <div className="text-emerald-700">Publicada</div>
                <div className="font-mono">{new Date(selected.activated_at).toLocaleString("es-ES")}</div>
                <div className="text-emerald-700">{selected.activated_by}</div>
              </div>
            ) : null}
            {selected.archived_at ? (
              <div className="rounded-md bg-neutral-100 border border-neutral-200 px-2 py-1.5">
                <div className="text-neutral-500">Archivada</div>
                <div className="font-mono">{new Date(selected.archived_at).toLocaleString("es-ES")}</div>
                <div className="text-neutral-500">{selected.archived_by}</div>
              </div>
            ) : null}
          </div>

          <div className="space-y-2" data-testid="chapters-container">
            {(draft.chapters || []).map((ch, idx) => (
              <ChapterRow
                key={ch.id}
                chapter={ch}
                index={idx}
                total={(draft.chapters || []).length}
                disabled={!isEditable}
                defaultOpen={idx === 0}
                onRename={renameChapter}
                onMove={moveChapter}
                onDuplicate={duplicateChapter}
                onDelete={deleteChapter}
                onSetClauses={setChapterClauses(ch.id)}
              />
            ))}
            {isEditable ? (
              <button
                type="button"
                onClick={addChapter}
                disabled={saving}
                className="w-full inline-flex items-center justify-center gap-1.5 rounded-xl border-2 border-dashed border-orange-400 bg-orange-50/70 hover:bg-orange-100 text-orange-800 px-4 py-3 text-sm font-semibold transition-colors disabled:opacity-50"
                data-testid="add-chapter-btn"
              >
                <Plus className="h-4 w-4" /> Añadir nuevo capítulo
              </button>
            ) : null}
          </div>

          <div className="sticky bottom-0 -mx-4 md:-mx-6 border-t border-neutral-200 bg-white/95 backdrop-blur px-4 md:px-6 py-3 flex items-center justify-between gap-2 flex-wrap">
            <div className="text-[11px] text-neutral-600">
              {isEditable ? (
                dirty
                  ? <span className="text-amber-700 font-semibold">● Cambios sin guardar</span>
                  : <span className="text-emerald-700">✓ Todo guardado</span>
              ) : (
                <span className="inline-flex items-center gap-1"><Lock className="h-3 w-3" /> Versión inmutable</span>
              )}
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              {isEditable ? (
                <>
                  <button
                    type="button"
                    onClick={handleDeleteDraft}
                    disabled={saving}
                    className="inline-flex items-center gap-1 rounded-md border border-red-300 hover:bg-red-50 text-red-700 px-3 py-1.5 text-xs font-semibold disabled:opacity-50"
                    data-testid="delete-draft-btn"
                  ><Trash2 className="h-3.5 w-3.5" /> Eliminar borrador</button>
                  <button
                    type="button"
                    onClick={handleSaveDraft}
                    disabled={saving || !dirty}
                    className="inline-flex items-center gap-1 rounded-md bg-neutral-800 hover:bg-neutral-900 text-white px-3 py-1.5 text-xs font-semibold disabled:opacity-50"
                    data-testid="save-draft-btn"
                  >
                    {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
                    Guardar borrador
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowActivate(true)}
                    disabled={saving || dirty}
                    title={dirty ? "Guarda antes de publicar" : ""}
                    className="inline-flex items-center gap-1 rounded-md bg-orange-600 hover:bg-orange-700 text-white px-3 py-1.5 text-xs font-semibold disabled:opacity-50"
                    data-testid="activate-btn"
                  ><Rocket className="h-3.5 w-3.5" /> Publicar versión</button>
                </>
              ) : null}
              {!isEditable && selected.status !== "active" ? (
                <button
                  type="button"
                  onClick={() => handleCreateDraftFrom(selected)}
                  disabled={saving}
                  className="inline-flex items-center gap-1 rounded-md border border-orange-500 text-orange-700 hover:bg-orange-50 px-3 py-1.5 text-xs font-semibold disabled:opacity-50"
                  data-testid="copy-to-draft-btn"
                ><FileText className="h-3.5 w-3.5" /> Crear borrador desde esta</button>
              ) : null}
            </div>
          </div>
        </>
      ) : null}

      <ActivateDialog
        open={showActivate}
        onClose={() => !activating && setShowActivate(false)}
        versionLabel={selected ? selected.version : ""}
        ackText={ackText}
        onConfirm={handleActivate}
        submitting={activating}
      />

      <AuditDialog
        open={showAudit}
        onClose={() => setShowAudit(false)}
        templateId={selected ? selected.id : null}
        versionLabel={selected ? selected.version : ""}
      />
    </div>
  );
}
